import { CharacterCounter, cn, IconButton } from '@form-factory/chatbot-ui';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  BadgePlus,
  CalendarRangeIcon,
  Loader2,
  Send,
  Square,
} from 'lucide-react';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useClassReservations } from '@/hooks/useClassReservations';
import { useBoundStore } from '@/store/store';

import { AccentButton } from '../bubble/AccentButton';

type InputBoxProps = {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onAbort?: () => void;
  value: string;
  isLoading?: boolean;
  isFetching?: boolean;
  disabledInput?: boolean;
  maxLength?: number;
};

export const InputBox = memo(
  ({
    onSubmit,
    onChange,
    onAbort,
    value,
    isLoading,
    isFetching,
    disabledInput,
    maxLength = 2000,
  }: InputBoxProps) => {
    const deferredValue = useDeferredValue(value);
    const { setIsInFAQLoop } = useBoundStore(
      useShallow(state => ({
        setIsInFAQLoop: state.setIsInFAQLoop,
      })),
    );
    const { user, pushRoute } = useBoundStore(
      useShallow(state => ({
        user: state.user,
        pushRoute: state.pushRoute,
      })),
    );
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isSubmitDisabled =
      ((isLoading || isFetching) && !onAbort) ||
      (deferredValue.length < 1 && !isLoading && !isFetching);

    // Get current reservations count
    const { reservationsCount } = useClassReservations();

    /**
     * Handle the submission of the input box.
     * If the input box is loading, it will abort the submission.
     * If the disclaimer is required but not acknowledged, focus the disclaimer checkbox.
     */
    const handleSubmit = useCallback(
      (event: React.FormEvent) => {
        // Don't do anything if there is no onAbort function
        if (!onAbort) {
          return;
        }

        // Prevent submission and abort
        if (isLoading || isFetching) {
          event.preventDefault();
          onAbort();
        }

        onSubmit(event as FormEvent<HTMLFormElement>);
        setIsInFAQLoop(false);
      },
      [onAbort, isLoading, isFetching, onSubmit, setIsInFAQLoop],
    );

    /**
     * Handles the auto-resizing of the textarea based on the content.
     */
    useEffect(() => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value]);

    /**
     * Submits the message when the Enter key is pressed without the Shift key.
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabledInput || isSubmitDisabled || isFetching) {
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    return (
      <form onSubmit={handleSubmit} className='flex flex-col gap-2'>
        <div className='relative'>
          <div
            className={cn(
              'grid cursor-text gap-2 overflow-hidden rounded-2xl bg-chat-input-background text-chat-input-foreground placeholder-chat-placeholder p-2 relative pb-10',
              disabledInput && 'opacity-70',
            )}
            onClick={() => !disabledInput && textareaRef.current?.focus()}
          >
            <textarea
              ref={textareaRef}
              rows={1}
              className='no-scrollbar max-h-[150px] min-h-8 flex-1 resize-none overflow-y-auto border-none bg-transparent pl-2 pr-11 pt-1 outline-none disabled:cursor-not-allowed'
              placeholder={
                isFetching
                  ? t`Waiting for a response...`
                  : isLoading
                    ? t`Loading conversation...`
                    : t`Type your question`
              }
              maxLength={maxLength}
              value={value}
              disabled={disabledInput}
              onChange={onChange}
              onKeyDown={handleKeyDown}
              data-testid='input-box'
            />
          </div>

          <div className='absolute inset-y-0 right-0 flex items-start pr-1 pt-1'>
            <IconButton
              type='submit'
              variant='primary'
              circle
              size='lg'
              disabled={isSubmitDisabled}
              data-testid='send-button'
              data-is-loading={isLoading || isFetching}
            >
              {isLoading && !isFetching ? (
                <Loader2 className='animate-spin' />
              ) : (isLoading || isFetching) && onAbort ? (
                <Square />
              ) : (
                <Send />
              )}
            </IconButton>
          </div>

          <div className='absolute bottom-2.5 left-2.5 flex w-[calc(100%-1.5rem)] items-end justify-between'>
            <div className='flex gap-2'>
              {user?.externalId && (
                <AccentButton onClick={() => pushRoute('newMembership')}>
                  <BadgePlus className='size-4' />
                  <Trans>New membership</Trans>
                </AccentButton>
              )}
              <AccentButton
                variant='secondary'
                onClick={() => pushRoute('groupClasses')}
              >
                <CalendarRangeIcon />
                <Trans>Group classes</Trans>
                {!!reservationsCount && (
                  <span className='flex h-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white'>
                    {reservationsCount}
                  </span>
                )}
              </AccentButton>
            </div>
            <CharacterCounter max={maxLength} current={deferredValue?.length} />
          </div>
        </div>

        <div
          className='flex flex-col items-center justify-between gap-2'
          data-testid='disclaimer-acknowledgement'
        >
          <div className='w-full text-center text-xs text-chat-muted-foreground'>
            <Trans>Content is generated using AI.</Trans>
          </div>
        </div>
      </form>
    );
  },
);
